/**
 * The SPA's write-through API client + demo auth token.
 *
 * Auth: locally the Vite dev proxy injects the Bearer token on every /api call,
 * so no token is needed here. In a deployed build the SPA obtains one via
 * `login()` (POST /api/auth/login) and it rides `authHeaders()` on every
 * request. Persistence is optimistic — a failed write leaves local state
 * standing. Each write reports a typed outcome so the write queue (writeQueue.ts)
 * can RETRY transient failures with backoff and, only once its retries are
 * exhausted, surface the loss loudly (surfaceWriteFailure). The next hydrate from
 * /api/bootstrap is the reconciliation point.
 */
import { usePersistenceStatus } from '@/state/persistenceStatus';
import { useUIStore } from '@/state/uiStore';

/**
 * The outcome the write queue keys its retry logic on:
 * - 'ok'        — the write landed (2xx).
 * - 'rejected'  — the server answered and authoritatively REFUSED it: 409
 *                 conflict, 422 domain validation, or any other 4xx. The caller
 *                 reverts; NOT retried. The API is up, so this is never shown as
 *                 "API offline" (tv-2na.8.5) — the chip carries the real reason.
 * - 'transient' — network / abort / timeout, or a 5xx server fault only: worth
 *                 retrying, and surfaced loudly by the queue only after its
 *                 retries are exhausted.
 */
export type PersistOutcome = 'ok' | 'transient' | 'rejected';

/** A write that gets no response within this window is aborted (→ a transient
 *  outcome the queue retries), rather than hanging on "Saving…" forever against a
 *  slow/cold backend. */
export const WRITE_TIMEOUT_MS = 8000;

/** An abort signal that fires after WRITE_TIMEOUT_MS, plus a clear() to cancel it
 *  once the write settles. No-op where AbortController is unavailable. */
function writeTimeout(): { signal?: AbortSignal; clear: () => void } {
  if (typeof AbortController === 'undefined') return { clear: () => {} };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), WRITE_TIMEOUT_MS);
  return { signal: ctrl.signal, clear: () => clearTimeout(timer) };
}

/**
 * Surface a genuinely-lost write loudly: flip the status chip to the red "write
 * failed" state and, on the transition into failure (deduped so a burst doesn't
 * spam), push a critical toast — so an edit that didn't persist never vanishes
 * silently. Called by the write queue AFTER its retries are exhausted
 * (writeQueue.ts) — NOT per attempt — so a transient blip that the retries
 * recovered stays quiet (amber "offline"). Exported for the queue's give-up path.
 */
export function surfaceWriteFailure(): void {
  const status = usePersistenceStatus.getState();
  const alreadyFailed = status.status === 'error';
  status.fail();
  if (!alreadyFailed) {
    try {
      useUIStore.getState().pushNotification({
        intensity: 'critical',
        title: "Couldn't save your change",
        body: "The server didn't confirm your last edit, so it may not be saved. Check your connection and try again.",
        ttlMs: 8000,
      });
    } catch {
      // uiStore unavailable (non-UI context / tests without it) — status still flips.
    }
  }
}

/**
 * Classify an HTTP status (or null for a thrown / aborted fetch) into a retry
 * outcome: 2xx ok; any 4xx is an authoritative refusal (the caller reverts);
 * no response or a 5xx is transient — retried by the queue, then surfaced loudly
 * on give-up — so a write is never silently dropped.
 */
export function classifyOutcome(status: number | null): PersistOutcome {
  if (status === null) return 'transient';
  if (status >= 200 && status < 300) return 'ok';
  if (status >= 400 && status < 500) return 'rejected';
  return 'transient';
}

/** The chip reason for a 409 (decided synchronously, without parsing the body). */
export const CONFLICT_REASON = 'Conflict: the server refused this change';

/** Best-effort parse of a refusal body ({ error, code, message }). */
function readRefusal(res: Response): Promise<{ error?: string; code?: string; message?: string } | undefined> {
  if (typeof res.json !== 'function') return Promise.resolve(undefined);
  return res.json().then(
    (b) => (b && typeof b === 'object' ? (b as { error?: string; code?: string; message?: string }) : undefined),
    () => undefined,
  );
}

/**
 * Settle a refused (4xx) write: flip the chip to 'rejected' with a status-based
 * reason at once, then upgrade it to the server's own message once the body
 * parses. 409 relays to `onReject` synchronously (no body parse) to preserve the
 * exact revert timing tv-1vh.11's tests depend on; every other 4xx relays the
 * parsed body so the revert toast can show `message`.
 */
function settleRefusal(
  res: Response,
  onReject?: (status: number, body: { message?: string } | undefined) => void,
): void {
  const { settle, setRejectReason } = usePersistenceStatus.getState();
  if (res.status === 409) {
    settle('rejected', CONFLICT_REASON);
    onReject?.(409, undefined);
    return;
  }
  settle('rejected', `Refused by the server (${res.status})`);
  void readRefusal(res).then((body) => {
    const reason = body?.message ?? body?.error;
    if (reason) setRejectReason(reason);
    onReject?.(res.status, body);
  });
}

const TOKEN_KEY = 'ati-auth-token';

let token: string | null = null;
try {
  // localStorage (not sessionStorage): the token is shared across every tab on
  // the origin and survives a tab close / redeploy, so a freshly opened tab
  // (e.g. /console) is already authenticated instead of 401ing (tv-k9b.23).
  token = localStorage.getItem(TOKEN_KEY);
} catch {
  // localStorage unavailable (private mode / tests) — token stays in memory.
}

export function setAuthToken(next: string | null): void {
  token = next;
  try {
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // best-effort persistence of the token; in-memory value still applies.
  }
}

export function getAuthToken(): string | null {
  return token;
}

/**
 * Holder for the shared demo unlock password. Mirrors the token pattern above:
 * localStorage-backed so a freshly opened tab stays unlocked, best-effort (falls
 * back to no-op / empty string when storage is unavailable in private mode or tests).
 */
const DEMO_PW_KEY = 'ati-demo-pw';
export function setDemoPassword(pw: string): void {
  try {
    localStorage.setItem(DEMO_PW_KEY, pw);
  } catch {
    /* in-memory only */
  }
}
export function getDemoPassword(): string {
  try {
    return localStorage.getItem(DEMO_PW_KEY) ?? '';
  } catch {
    return '';
  }
}

export function authHeaders(): Record<string, string> {
  return token ? { authorization: `Bearer ${token}` } : {};
}

/**
 * The sign-in in flight, if any (B3). The server checks who sends each write, so
 * a write queued while the persona is being switched waits for the new token
 * instead of going out under the previous persona's. Null when idle, so writes
 * outside a switch are sent exactly as before (synchronously).
 */
let signingIn: Promise<boolean> | null = null;

/** Send `write` now, or once the in-flight sign-in settles (never for keepalive). */
function afterSignIn<T>(write: () => Promise<T>, keepalive?: boolean): Promise<T> {
  return signingIn && !keepalive ? signingIn.then(write, write) : write();
}

/**
 * Resolves once no sign-in is in flight. Intents await it (only when isSigningIn(),
 * so an idle call still fetches synchronously) before reading the token, like the
 * write-through PUTs do (afterSignIn).
 */
export function isSigningIn(): boolean {
  return signingIn !== null;
}

export function whenSignedIn(): Promise<void> {
  return signingIn ? signingIn.then(() => undefined, () => undefined) : Promise.resolve();
}

/**
 * Minimal demo login: mint + store a persona token. Returns success. Only the
 * LATEST sign-in may store its token: switching A -> B quickly must never leave
 * A's slower response as the tab's token while it shows B.
 */
export function login(
  persona: string,
  studio?: string,
  password?: string,
  who?: { pid?: string; name?: string },
  opts?: { timeoutMs?: number },
): Promise<boolean> {
  const attempt: Promise<boolean> = mintToken(persona, studio, password, who, opts?.timeoutMs).then((minted) => {
    if (signingIn === attempt) {
      if (minted) setAuthToken(minted);
      // A failed latest sign-in drops the previous token for THIS page (tv-v28k):
      // the screen already shows the new persona, so writes are refused rather
      // than recorded as the previous person. The stored token is left alone: a
      // reload mid-switch cancels the login, and must not come back signed out.
      else token = null;
    }
    return minted !== null;
  });
  signingIn = attempt;
  void attempt.finally(() => {
    if (signingIn === attempt) signingIn = null;
  });
  return attempt;
}

/** A persona switch that gets no answer within this window is given up (tv-v28k),
 *  so the writes held behind it are released instead of waiting forever on a bad
 *  connection. Staging logins have taken 10-60s on a slow hotspot, hence the
 *  generous limit. */
export const SIGN_IN_TIMEOUT_MS = 45000;

/** POST /api/auth/login; the minted token, or null on a refusal / network miss / timeout. */
async function mintToken(
  persona: string,
  studio?: string,
  password?: string,
  who?: { pid?: string; name?: string },
  timeoutMs?: number,
): Promise<string | null> {
  const ctrl = timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : undefined;
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      ...(ctrl ? { signal: ctrl.signal } : {}),
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        persona,
        ...(studio ? { studio } : {}),
        ...(password ? { password } : {}),
        ...(who?.pid ? { pid: who.pid } : {}),
        ...(who?.name ? { name: who.name } : {}),
      }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { token?: unknown };
    return typeof body.token === 'string' ? body.token : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * `onReject` fires when the server refuses a write in a way that must NOT stand
 * (the optimistic local value is actively wrong): a 409 booking-conflict, a
 * 422 domain-validation rejection, or any other 4xx refusal (tv-2na.8.5) (tv-ods.1 — an invariant the server enforces:
 * illegal status transition, money that doesn't reconcile, self-approval, …).
 * The caller (the persistence bridge) owns what to do — this module just relays
 * the server's answer + reason. `body.message` is the server's human message.
 *
 * 409 fires synchronously (no body parse) to preserve the exact revert timing
 * tv-1vh.11's tests depend on; other 4xx parse the JSON body to surface `message`.
 *
 * Returns a PersistOutcome so the write queue can retry a transient miss and
 * leave the loud surface to its give-up path.
 */
export function persistRecord(
  entity: string,
  id: string,
  record: unknown,
  onReject?: (status: number, body: { message?: string } | undefined) => void,
  opts?: { keepalive?: boolean },
): Promise<PersistOutcome> {
  const { begin, settle } = usePersistenceStatus.getState();
  begin();
  // A keepalive write is an unload/tab-hide best-effort flush: no timeout (that
  // would defeat keepalive) and the queue never retries it (the page is going away).
  // The timer starts when the write is sent, not while it waits for a sign-in, so a
  // held write isn't already aborted when it finally goes (tv-v28k).
  let to: { signal?: AbortSignal; clear: () => void } = { clear: () => {} };
  return afterSignIn(
    () => {
      if (!opts?.keepalive) to = writeTimeout();
      return fetch(`/api/state/${entity}/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json', ...authHeaders() },
        body: JSON.stringify(record),
        ...(opts?.keepalive ? { keepalive: true } : {}),
        ...(to.signal ? { signal: to.signal } : {}),
      });
    },
    opts?.keepalive,
  )
    .then((res): PersistOutcome => {
      to.clear();
      const outcome = classifyOutcome(res.status);
      // A refusal (409 / 422 / any 4xx) is authoritative — settle as 'rejected'
      // with its reason and let the caller revert; the queue must NOT retry it.
      if (outcome === 'rejected') {
        settleRefusal(res, onReject);
        return 'rejected';
      }
      // 2xx lands; a 5xx is a transient miss — settle quietly (amber "offline");
      // the queue retries and only surfaces loudly once exhausted.
      settle(outcome);
      return outcome;
    })
    .catch((): PersistOutcome => {
      // Network error / aborted timeout — transient; settle quietly and let the
      // queue retry (the loud surface is the queue's give-up concern).
      to.clear();
      settle('transient');
      return 'transient';
    });
}

/**
 * Persist an artist edit to its own table. Artists are server-owned reference
 * data in `app.artists` (not the generic `app.spa_state` the write-through
 * bridge targets), so they need this dedicated route: `PUT /api/artists/:id`
 * re-serialises the whole artist, `rates` jsonb included (which now also carries
 * the per-tier deposit fields). Optimistic like `persistRecord`: the in-memory
 * update stands and this makes the outcome visible; in dev with no API the
 * fetch rejects and it settles offline without throwing. After a successful
 * write a reload (bootstrap reads `app.artists`) reflects the edit.
 */
export function persistArtist(
  /** The artist record; `commissionChange: true` marks an explicit commission save (B9 / 75nk). */
  artist: { id: string; commissionChange?: boolean },
  opts?: {
    /** A refused (4xx) write: the status and the server's `{ message }`, so the caller can revert and say why. */
    onReject?: (status: number, body: { message?: string } | undefined) => void;
  },
): Promise<PersistOutcome> {
  const { begin, settle } = usePersistenceStatus.getState();
  begin();
  // Waits for an in-flight sign-in like persistRecord: the server checks the
  // commission against the token's persona, so a write must carry the new token.
  return afterSignIn(() =>
    fetch(`/api/artists/${encodeURIComponent(artist.id)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(artist),
    }),
  )
    .then((res): PersistOutcome => {
      const outcome = classifyOutcome(res.status);
      if (outcome === 'rejected') settleRefusal(res, opts?.onReject);
      else settle(outcome);
      return outcome;
    })
    .catch((): PersistOutcome => {
      settle('transient');
      return 'transient';
    });
}

export function deleteRecord(entity: string, id: string, opts?: { keepalive?: boolean }): Promise<PersistOutcome> {
  const { begin, settle } = usePersistenceStatus.getState();
  begin();
  // The timer starts when the write is sent, not while it waits for a sign-in, so a
  // held write isn't already aborted when it finally goes (tv-v28k).
  let to: { signal?: AbortSignal; clear: () => void } = { clear: () => {} };
  return afterSignIn(
    () => {
      if (!opts?.keepalive) to = writeTimeout();
      return fetch(`/api/state/${entity}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { ...authHeaders() },
        ...(opts?.keepalive ? { keepalive: true } : {}),
        ...(to.signal ? { signal: to.signal } : {}),
      });
    },
    opts?.keepalive,
  )
    // Optimistic — see persistRecord.
    .then((res): PersistOutcome => {
      to.clear();
      const outcome = classifyOutcome(res.status);
      if (outcome === 'rejected') settleRefusal(res);
      else settle(outcome);
      return outcome;
    })
    .catch((): PersistOutcome => {
      to.clear();
      settle('transient');
      return 'transient';
    });
}
